import { z } from 'zod'
import { BikeStatus, BikeType } from '../enums'
import { Id, Timestamp } from './common'

export const Bike = z.object({
  id: Id,
  name: z.string(),
  type: BikeType,
  brand: z.string().nullable(),
  model: z.string().nullable(),
  year: z.number().int().nullable(),
  frame_size: z.string().nullable(),
  frame_serial: z.string().nullable(),
  /** Minor units (pence). */
  purchase_price_minor: z.number().int().nullable(),
  currency: z.string().nullable(),
  purchase_date: z.string().nullable(),
  sold_at: Timestamp.nullable(),
  sale_price_minor: z.number().int().nullable(),
  status: BikeStatus,
  hero_photo_id: Id.nullable(),
  /** Signed thumbnail URL of the hero photo, when it has been processed. */
  hero_thumb_url: z.string().nullable(),
  strava_gear_id: z.string().nullable(),
  created_at: Timestamp,
  updated_at: Timestamp,
})
export type Bike = z.infer<typeof Bike>

export const BikeList = z.object({ bikes: z.array(Bike) })
export type BikeList = z.infer<typeof BikeList>

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional()

export const CreateBikeBody = z.object({
  name: z.string().trim().min(1, 'Give the bike a name.').max(80),
  type: BikeType.default('other'),
  brand: optionalText(80),
  model: optionalText(80),
  year: z.number().int().min(1860).max(2100).nullable().optional(),
  frame_size: optionalText(20),
  frame_serial: optionalText(80),
  purchase_price_minor: z.number().int().min(0).max(100_000_000).nullable().optional(),
  currency: z
    .string()
    .regex(/^[A-Z]{3}$/, 'Use a three-letter currency code, such as GBP.')
    .nullable()
    .optional(),
  purchase_date: z.iso.date().nullable().optional(),
  status: BikeStatus.default('active'),
})
export type CreateBikeBody = z.input<typeof CreateBikeBody>

export const UpdateBikeBody = CreateBikeBody.partial()
  .extend({
    name: z.string().trim().min(1, 'Give the bike a name.').max(80).optional(),
    type: BikeType.optional(),
    status: BikeStatus.optional(),
  })
  .refine((b) => Object.keys(b).length > 0, 'Nothing to update.')
export type UpdateBikeBody = z.input<typeof UpdateBikeBody>

/**
 * Sold it. Parts fitted at the time go with the bike: their installs close at
 * `sold_at` and they are marked sold. All history stays in the garage.
 */
export const SoldBody = z.object({
  /** Defaults to now. Not in the future. */
  sold_at: Timestamp.optional(),
  sale_price_minor: z.number().int().min(0).max(100_000_000).nullable().optional(),
})
export type SoldBody = z.input<typeof SoldBody>
